import React, { useState, useEffect } from 'react';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { paymentService } from '../../services/ShipmentPaymentService.js';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import { Plus, MapPin, Pencil, CreditCard, Trash2, TrendingUp, FileDown, Eye, X } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { downloadBlob } from '../../utils/downloadBlob.js';

const REMARK_PREVIEW_MAX = 48;

function PaymentRemarkCell({ remark, onViewFull, singleLine = false }) {
  const text = remark?.trim() || '';
  if (!text) {
    return <span className="text-sm text-gray-500">N/A</span>;
  }

  const isLong = text.length > REMARK_PREVIEW_MAX;
  const preview = isLong ? `${text.slice(0, REMARK_PREVIEW_MAX).trimEnd()}…` : text;

  return (
    <div className={`flex min-w-0 gap-1.5 ${singleLine ? 'items-center w-full' : 'items-start max-w-xs'}`}>
      <span
        className={`flex-1 min-w-0 ${
          singleLine
            ? 'truncate text-xs sm:text-sm text-gray-900'
            : 'text-sm text-gray-600 leading-snug line-clamp-2 break-words'
        }`}
      >
        {preview}
      </span>
      {isLong && (
        <button
          type="button"
          onClick={() => onViewFull(text)}
          className="shrink-0 p-1 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors"
          title="View full remark"
          aria-label="View full remark"
        >
          <Eye className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}


export default function PaymentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedShipmentDetails, setSelectedShipmentDetails] = useState(null);
  const [viewRemarkText, setViewRemarkText] = useState(null);
  const perPage = 10;

  const isAdminRole = user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant' || user?.role === 'Hub Admin';
  const isAgent = user?.role === 'Agent';

  useEffect(() => {
    if (user) {
      fetchPayments();
    }
  }, [currentPage, filter, userFilter, fromDate, toDate, search, user]);

  useEffect(() => {
    if (user && isAdminRole) {
      fetchUsers();
    }
  }, [user, isAdminRole]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const fetchUsers = async () => {
    try {
      const response = await paymentService.getDistinctUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      let response;
      
      const params = {
        page: currentPage,
        per_page: perPage,
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
        ...(userFilter && { user_id: userFilter }),
        ...(search && { search }),
      };

      if (filter === 'pending') {
        response = await paymentService.getPendingPayments(params);
        setPayments((prev) => mergeLoadedPage(currentPage, response.data.data || [], prev));
      } else {
        response = await paymentService.getAllPaymentHistory(params);
        setPayments((prev) => mergeLoadedPage(currentPage, response.data.data || [], prev));
      }
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching payments:', error);
      Alert.error(error.response?.data?.error || 'Failed to fetch payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (payment) => {
    const confirmed = await Alert.confirm(
      `Delete payment record of ₹${payment.payment_amount?.toFixed(2)} for shipment ${payment.shipment_id}?`,
      'Delete Payment Record'
    );

    if (!confirmed) return;

    try {
      await paymentService.deletePayment(payment.id);
      Alert.success('Payment record deleted successfully');
      fetchPayments();
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to delete payment');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams({
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
        ...(userFilter && { user_id: userFilter })
      });

      const response = await paymentService.exportPaymentHistoryCSV(params.toString());
      await downloadBlob(
        response.data,
        `payment_history_${new Date().getTime()}.csv`,
        'text/csv',
      );
      
      Alert.success('Payment history exported successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      Alert.error('Failed to export payment history');
    } finally {
      setExporting(false);
    }
  };

  const handleViewShipmentPayments = async (shipmentId) => {
    try {
      const response = await paymentService.getShipmentPaymentDetails(shipmentId);
      setSelectedShipmentDetails(response.data.data);
      setShowPaymentModal(true);
    } catch (error) {
      Alert.error('Failed to load shipment payment details');
    }
  };

  const toggleMenu = (paymentId) => {
    setOpenMenuId(openMenuId === paymentId ? null : paymentId);
  };

  const hasActiveFilters = userFilter || fromDate || toDate || search || filter !== 'all';

  const clearFilters = () => {
    setUserFilter('');
    setFromDate('');
    setToDate('');
    setSearch('');
    setCurrentPage(1);
  };

  // Show loading while user is being loaded
  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading payment history..." />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
              <div className="max-w-full mx-auto">
                <ListPageHeader
                  title="Payment History"
                  icon={CreditCard}
                  iconClassName="w-7 h-7 text-buttonhoverTxt shrink-0"
                  className="mb-6 sm:mb-4"
                >
                  <Button mobileIconOnly variant="primary" icon={TrendingUp} onClick={() => navigate('/daily-earnings')}>
                    Daily Earnings
                  </Button>
                  <Button
                    mobileIconOnly
                    variant="secondary"
                    icon={FileDown}
                    onClick={handleExport}
                    disabled={exporting || payments.length === 0}
                    loading={exporting}
                    mobileLabel={exporting ? 'Exporting...' : 'Export CSV'}
                  >
                    {exporting ? 'Exporting...' : 'Export CSV'}
                  </Button>
                </ListPageHeader>

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <SearchSubmitInput
                      value={search}
                      onSearch={(value) => {
                        setSearch(value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search by shipment ID..."
                      inputClassName="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <select
                      value={filter}
                      onChange={(e) => {
                        setFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    >
                      <option value="all">All Payments</option>
                      <option value="pending">Pending Payments</option>
                    </select>
                  </div>

                  <div>
                    <input
                      type="date"
                      placeholder="From Date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <input
                      type="date"
                      placeholder="To Date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>

                  {isAdminRole && users.length > 0 && (
                    <div>
                      <select
                        value={userFilter}
                        onChange={(e) => {
                          setUserFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                      >
                        <option value="">All Agents</option>
                        {users.map(u => (
                          <option key={u.user_id} value={u.user_id}>
                            {u.name} ({u.user_id})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </ListFilterPanel>

                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading payments..." />
                  </div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="text-6xl mb-4">🔭</div>
                    <p className="text-gray-600 text-lg font-semibold">No payments found</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden lg:block">
                      <div className="bg-white rounded-xl shadow-sm">
                        <div className="min-w-full">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipment ID</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Mode</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pre. Balance</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Balance</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recorded By</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] max-w-xs">Remarks</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {payments.map((payment) => (
                                <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <Button variant="link" onClick={() => handleViewShipmentPayments(payment.shipment_id)} className={listIdLinkClass}>
                                      {payment.shipment_id}
                                    </Button>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    ₹{payment.payment_amount?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 uppercase">
                                    {payment.payment_mode?.replace(/_/g, ' ') || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    ₹{payment.previous_balance?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    ₹{payment.new_balance?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    }) : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {payment.recorded_by_name || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 align-top min-w-[140px] max-w-xs">
                                    <PaymentRemarkCell
                                      remark={payment.remarks}
                                      onViewFull={setViewRemarkText}
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <RowActionsMenu
                                      className="relative menu-container"
                                      isOpen={openMenuId === payment.id}
                                      onToggle={() => toggleMenu(payment.id)}
                                      onClose={() => setOpenMenuId(null)}
                                      onView={() => navigate(`/shipments/view/${payment.shipment_id}`)}
                                      viewLabel="View Shipment"
                                      onEdit={() => navigate(`/payments/edit/${payment.id}`)}
                                      editLabel="Edit Payment"
                                      onDelete={() => handleDelete(payment)}
                                      deleteLabel="Delete Payment"
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Desktop Pagination */}
                        <div className="bg-gray-50 px-6 py-4 border-t flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            Showing <span className="font-semibold">{payments.length}</span> payments
                          </span>
                          <ListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={loading && currentPage > 1}
          onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
        />
                        </div>
                      </div>
                    </div>

                    {/* Mobile Cards */}
                    <div className="lg:hidden space-y-3">
                      {payments.map((payment) => (
                        <div key={payment.id} className="bg-white rounded-xl shadow-md p-4">
                          <div className="flex justify-between items-start gap-2 mb-3">
                            <div className="min-w-0">
                              <Button variant="link" onClick={() => handleViewShipmentPayments(payment.shipment_id)} className={`${listIdLinkMobileClass} p-0`}>
                                {payment.shipment_id}
                              </Button>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase whitespace-nowrap">
                                {payment.payment_mode?.replace(/_/g, ' ')}
                              </span>
                              <RowActionsMenu
                                className="relative menu-container"
                                isOpen={openMenuId === payment.id}
                                onToggle={() => toggleMenu(payment.id)}
                                onClose={() => setOpenMenuId(null)}
                                onView={() => navigate(`/shipments/view/${payment.shipment_id}`)}
                                viewLabel="View Shipment"
                                onEdit={() => navigate(`/payments/edit/${payment.id}`)}
                                editLabel="Edit Payment"
                                onDelete={() => handleDelete(payment)}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                            <div>
                              <span className="text-gray-500">Amount:</span>
                              <p className="font-bold text-green-600">₹{payment.payment_amount?.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Prev Balance:</span>
                              <p className="font-semibold text-gray-900">₹{payment.previous_balance?.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">New Balance:</span>
                              <p className="font-bold text-red-600">₹{payment.new_balance?.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="text-gray-900">
                                {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                }) : 'N/A'}
                              </p>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-500">Recorded By:</span>
                              <p className="font-medium text-gray-900">{payment.recorded_by_name || 'N/A'}</p>
                            </div>
                            {payment.remarks?.trim() && (
                              <div className="col-span-2 min-w-0">
                                <span className="text-gray-500">Remarks:</span>
                                <div className="mt-0.5">
                                  <PaymentRemarkCell
                                    remark={payment.remarks}
                                    onViewFull={setViewRemarkText}
                                    singleLine
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      
                    </div>
                  </>
                )}
              </div>

      {/* Payment Details Modal */}
      {showPaymentModal && selectedShipmentDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-base sm:text-lg font-semibold text-gray-700">Shipment Payment History</h2>
              <Button variant="ghost" onClick={() => setShowPaymentModal(false)} className="text-gray-500 hover:text-gray-700 text-3xl">
                ×
              </Button>
            </div>
            
            <div className="p-6">
              {/* Shipment Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Shipment Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Shipment ID:</span>
                    <span className="font-bold text-orange-600 ml-2">{selectedShipmentDetails.shipment.shipment_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Sender:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.sender_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Receiver:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.receiver_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Agent:</span>
                    <span className="font-bold ml-2">{selectedShipmentDetails.shipment.agent_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-bold text-blue-600 ml-2">₹{selectedShipmentDetails.shipment.total_amount?.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Paid Amount:</span>
                    <span className="font-bold text-green-600 ml-2">₹{selectedShipmentDetails.shipment.paid_amount?.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Balance:</span>
                    <span className="font-bold text-red-600 ml-2">₹{selectedShipmentDetails.shipment.balance_amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment History Table */}
              <div>
                <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-2 pb-2">Payment History</h3>
                {selectedShipmentDetails.payment_history.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No payment records found</p>
                ) : (
                  <>
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left font-bold text-gray-700">Date</th>
                            <th className="px-4 py-2 text-left font-bold text-gray-700">Amount</th>
                            <th className="px-4 py-2 text-left font-bold text-gray-700">Mode</th>
                            <th className="px-4 py-2 text-left font-bold text-gray-700">Previous Balance</th>
                            <th className="px-4 py-2 text-left font-bold text-gray-700">New Balance</th>
                            <th className="px-4 py-2 text-left font-bold text-gray-700">Recorded By</th>
                            <th className="px-4 py-2 text-left font-bold text-gray-700 min-w-[140px] max-w-xs">Remarks</th>
                            {!isAgent && (
                              <th className="px-4 py-2 text-left font-bold text-gray-700">{t('common.actions')}</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {selectedShipmentDetails.payment_history.map((payment) => (
                            <tr key={payment.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                {new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </td>
                              <td className="px-4 py-2 font-bold text-green-600">
                                ₹{payment.payment_amount?.toFixed(2)}
                              </td>
                              <td className="px-4 py-2">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase">
                                  {payment.payment_mode?.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="px-4 py-2">₹{payment.previous_balance?.toFixed(2)}</td>
                              <td className="px-4 py-2 font-bold text-red-600">
                                ₹{payment.new_balance?.toFixed(2)}
                              </td>
                              <td className="px-4 py-2">{payment.recorded_by_name}</td>
                              <td className="px-4 py-2 align-top min-w-[140px] max-w-xs">
                                <PaymentRemarkCell
                                  remark={payment.remarks}
                                  onViewFull={setViewRemarkText}
                                />
                              </td>
                              {!isAgent && (
                                <td className="px-4 py-2">
                                  <div className="flex gap-2">
                                    <Button variant="ghost" onClick={() => { setShowPaymentModal(false); navigate(`/payments/edit/${payment.id}`); }} className="p-1.5 text-blue-600 hover:bg-blue-50">
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" onClick={() => handleDelete(payment)} className="p-1.5 text-red-600 hover:bg-red-50">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="md:hidden space-y-3">
                      {selectedShipmentDetails.payment_history.map((payment) => (
                        <div key={payment.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                          <div className="flex justify-between items-start gap-2 mb-3">
                            <p className="text-sm text-gray-700 font-medium">
                              {new Date(payment.payment_date).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase whitespace-nowrap">
                                {payment.payment_mode?.replace(/_/g, ' ')}
                              </span>
                              {!isAgent && (
                                <RowActionsMenu
                                  isOpen={openMenuId === `modal-${payment.id}`}
                                  onToggle={() => toggleMenu(`modal-${payment.id}`)}
                                  onClose={() => setOpenMenuId(null)}
                                  onEdit={() => { setShowPaymentModal(false); navigate(`/payments/edit/${payment.id}`); }}
                                  editLabel="Edit Payment"
                                  onDelete={() => handleDelete(payment)}
                                />
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                            <div>
                              <span className="text-gray-500">Amount:</span>
                              <p className="font-bold text-green-600">₹{payment.payment_amount?.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Prev Balance:</span>
                              <p className="font-medium text-gray-900">₹{payment.previous_balance?.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">New Balance:</span>
                              <p className="font-bold text-red-600">₹{payment.new_balance?.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Recorded By:</span>
                              <p className="font-medium text-gray-900 truncate">{payment.recorded_by_name}</p>
                            </div>
                            {payment.remarks?.trim() && (
                              <div className="col-span-2 min-w-0">
                                <span className="text-gray-500">Remarks:</span>
                                <div className="mt-0.5">
                                  <PaymentRemarkCell
                                    remark={payment.remarks}
                                    onViewFull={setViewRemarkText}
                                    singleLine
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3">
                <Button variant="primary" onClick={() => navigate(`/shipments/view/${selectedShipmentDetails.shipment.shipment_id}`)}>
                  View Shipment Details
                </Button>
                <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewRemarkText && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={() => setViewRemarkText(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="remark-modal-title"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 id="remark-modal-title" className="text-base font-semibold text-gray-800">
                Remark
              </h3>
              <button
                type="button"
                onClick={() => setViewRemarkText(null)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed max-h-[50vh] overflow-y-auto">
              {viewRemarkText}
            </p>
            <div className="mt-4 flex justify-end">
              <Button variant="secondary" type="button" onClick={() => setViewRemarkText(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}